import { OktaMetaData } from 'module/api/account/types';
import { CONFIG } from 'module/authentication/okta/config';
import { transformOktaAuthenticationState } from 'module/authentication/token/okta-authentication-state';
import { LoginWidgetLinks } from 'module/authentication/types';
import { getEnabledIdp } from 'module/authentication/utils/saml';
import { useTranslation } from 'module/hooks/use-translation';
import { BodyShort01 } from 'module/ui/components/styles';
import { useOktaAuth } from '@okta/okta-react';
import OktaSignIn, { RenderResultSuccess } from '@okta/okta-signin-widget';
import '@okta/okta-signin-widget/css/okta-sign-in.min.css';
import { FC, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SsoSamlButton } from '../saml-button/button';

interface OktaSignInWidgetProps {
  oktaMetaData?: OktaMetaData;
  links: LoginWidgetLinks;
  className?: string;
}
const SignInWidget: FC<React.PropsWithChildren<OktaSignInWidgetProps>> = ({
  oktaMetaData,
  links,
  className,
}) => {
  const signWithSSO = useTranslation('sign_in.sso');
  const { oktaAuth } = useOktaAuth();

  useEffect(() => {
    if (oktaMetaData == null) {
      return;
    }
    const { pkce, issuer, redirectUri, scopes, clientId } = CONFIG({
      issuer: oktaMetaData.issuerDomain,
      clientId: oktaMetaData.clientId,
      transformAuthState: transformOktaAuthenticationState,
      postLoginRedirectUri: links.postLoginRedirectUri,
      postLogoutRedirectUri: links.postLogoutRedirectUri,
    });
    const widget = new OktaSignIn({
      /**
       * Note: when using the Sign-In Widget for an OIDC flow, it still
       * needs to be configured with the base URL for your Okta Org. Here
       * we derive it from the given issuer for convenience.
       */
      baseUrl: issuer.split('/oauth2')[0],
      clientId,
      redirectUri,
      features: {
        showPasswordToggleOnSignInPage: true,
      },
      useClassicEngine: true,
      i18n: {
        en: {
          'primaryauth.title': 'Sign in',
          'primaryauth.username.placeholder': 'Email',
          'primaryauth.username.tooltip': 'Your email id',
          'error.username.required': 'Please enter an email id',
          'errors.E0000004': 'Invalid email or password',
        },
      },
      authParams: {
        pkce,
        issuer,
        display: 'page',
        responseMode: pkce ? 'query' : 'fragment',
        scopes,
      },
      transformUsername: (username: string) => {
        if (oktaMetaData.userName && username.includes(oktaMetaData.userName)) {
          return `${username.replaceAll(oktaMetaData.userName, '')}${
            oktaMetaData.userName || ''
          }`;
        }

        return `${username}${oktaMetaData.userName || ''}`;
      },
    });

    widget.on('ready', () => {
      const emailInput = document.querySelector<HTMLInputElement>(
        '#okta-signin-username',
      );

      if (
        oktaMetaData.userName &&
        emailInput?.value.includes(oktaMetaData.userName)
      ) {
        emailInput.setAttribute(
          'value',
          emailInput.value.replaceAll(oktaMetaData.userName, ''),
        );
      }
    });
    widget.renderEl(
      { el: '#sign-in-widget' },
      (res) => {
        oktaAuth.handleLoginRedirect((res as RenderResultSuccess).tokens);
      },
      (err: Error) => {
        throw err;
      },
    );

    return () => {
      widget.remove();
    };
  }, [
    links.postLoginRedirectUri,
    links.postLogoutRedirectUri,
    oktaAuth,
    oktaMetaData,
    signWithSSO,
  ]);

  return <div className={className} id="sign-in-widget" />;
};

interface SignInWidgetProps extends OktaSignInWidgetProps {
  isSamlMandatory: boolean;
  isPartner: boolean;
}

const Hint = styled.p`
  ${BodyShort01};
  color: var(--gray-50);
`;

const Wrapper = styled.div`
  display: flex;
  gap: var(--spacing-03);
  align-items: stretch;
  flex-direction: column;
`;

export const OktaSignInWidget: FC<
  React.PropsWithChildren<SignInWidgetProps>
> = ({ oktaMetaData, className, isSamlMandatory, isPartner, links }) => {
  const { oktaAuth } = useOktaAuth();
  const idp = getEnabledIdp(oktaMetaData);

  if (isSamlMandatory && idp && !isPartner) {
    return (
      <Wrapper>
        <SsoSamlButton oktaAuth={oktaAuth} idp={idp} isCognitoEnabled={false} />
        <Hint>
          <FormattedMessage id="sign_in.sso.mandate.hint" />
        </Hint>
      </Wrapper>
    );
  }

  return (
    <SignInWidget
      oktaMetaData={oktaMetaData}
      className={className}
      links={links}
    />
  );
};
